import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useConferenceStore } from '@/stores/conference.store';
import { useVoipStore } from '@/stores/voip.store';
import { conferenceApi } from './api';
import { endTeamCall } from './endTeamCall';
import { useConferenceState, useRoster } from './hooks';
import { callWrapUp, isConferenceOver } from './state';
import type { ConferenceParticipant } from './types';

/**
 * How long a room may sit INITIATING after we dialled in. The service answers
 * our leg the moment it arrives, so this connects in seconds or it is broken —
 * the same budget the mobile app gives it.
 */
const CONNECT_TIMEOUT_MS = 30_000;

/**
 * How long "nobody left to talk to" has to hold before the call wraps up. The
 * service dials a group's members one after another and forks each across
 * their devices, so for a moment every leg dialled so far can be gone while the
 * next is about to be placed.
 */
const WRAP_UP_GRACE_MS = 4_000;

/**
 * Keeps this tab's own leg and the room it dialled into in step, so neither is
 * left behind by the other:
 *
 * - the room is over (see `isConferenceOver`) → hang up our leg;
 * - our leg drops before the room ever went live → end the room, or it would
 *   sit waiting for a caller who is gone;
 * - the room never goes live → give up with an explanation instead of leaving
 *   "Connecting…" on screen until the dial code lapses;
 * - nobody could be reached, or the other person in a one-to-one hung up →
 *   say so and end the call (see `callWrapUp`).
 */
export function useConferenceLegWatch(conferenceId: string | null) {
  const { data } = useConferenceState(conferenceId);
  const status = data?.conference.status;
  const over = isConferenceOver(data);
  const legUp = useVoipStore((s) => Object.values(s.calls).some((c) => c.conference));
  const hadLeg = useRef(false);
  const gaveUp = useRef<string | null>(null);
  const { data: roster } = useRoster();

  const invitedUserIds = useConferenceStore((s) => s.invitedUserIds);
  const wrapUp = callWrapUp(data, invitedUserIds);
  // A stable key, so polling the same outcome doesn't restart the grace timer.
  const wrapUpKey = wrapUp
    ? `${wrapUp.reason}:${wrapUp.legs.map((leg) => leg.callControlId).join(',')}`
    : null;
  const latest = useRef({ wrapUp, roster });
  useEffect(() => {
    latest.current = { wrapUp, roster };
  });

  useEffect(() => {
    hadLeg.current = false;
    gaveUp.current = null;
  }, [conferenceId]);

  useEffect(() => {
    if (legUp) hadLeg.current = true;
  }, [legUp]);

  useEffect(() => {
    if (over) useVoipStore.getState().hangupConferenceLegs();
  }, [over]);

  useEffect(() => {
    if (!conferenceId || status !== 'INITIATING') return;
    const giveUp = (message: string) => {
      if (gaveUp.current === conferenceId) return;
      gaveUp.current = conferenceId;
      useVoipStore.getState().hangupConferenceLegs();
      void conferenceApi.end(conferenceId).catch(() => undefined);
      toast.error(message);
    };
    if (hadLeg.current && !legUp) {
      giveUp("Couldn't connect the call. Your line dropped before anyone joined.");
      return;
    }
    const timer = setTimeout(
      () => giveUp("Couldn't connect the call. The call room never answered."),
      CONNECT_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [conferenceId, legUp, status]);

  useEffect(() => {
    if (!conferenceId || !wrapUpKey) return;
    const timer = setTimeout(() => {
      const current = latest.current.wrapUp;
      if (!current) return;
      const names = namesOf(current.legs, latest.current.roster);
      toast(
        current.reason === 'left'
          ? `${names[0] ?? 'Your teammate'} left the call.`
          : names.length === 1
            ? `Couldn't reach ${names[0]}. They may be offline.`
            : "Couldn't reach anyone. They may be offline.",
      );
      endTeamCall(conferenceId);
    }, WRAP_UP_GRACE_MS);
    return () => clearTimeout(timer);
  }, [conferenceId, wrapUpKey]);
}

/** One name per person — a member dialled on two devices has two legs. */
function namesOf(
  legs: ConferenceParticipant[],
  roster: Array<{ userId: string; name: string }> | undefined,
): string[] {
  const byId = new Map((roster ?? []).map((member) => [member.userId, member.name]));
  const names = legs.map((leg) => (leg.userId ? byId.get(leg.userId) : null) ?? null);
  return [...new Set(names.filter((name): name is string => Boolean(name)))];
}
